import { LanguagesEnum } from '@/api/enums';
import {
  Campaign,
  CampaignEmailingEnum,
  CampaignFormValues,
  CampaignPhishingSignEnum,
  CampaignStatusesEnum,
  CampaignStatusMessages,
  CampaignTemplate,
  CampaignType,
  CampaignUserResultMessages,
  CampaignUserResultStatusesEnum,
  SaveCampaignRequest,
  SenderProfileType,
} from '@/client/campaigns';
import { CourseEntityEnrollEnum } from '@/client/courses';
import { TargetEntity, TranslationFunctionType } from '@/common/types';
import {
  checkIfDateIsPastDate,
  checkIfDatesAreEqual,
  checkIfNowOrAfter,
} from '@/utils/helpers/dates.helper';
import { faker } from '@faker-js/faker';
import DOMPurify from 'dompurify';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { getTargetEntityIdsByType } from '../targets';

const DEFAULT_UNWANTED_TAGS = [
  'meta',
  'title',
  'img',
  'link',
  'script',
  'base',
  'head',
  'html',
  'body',
  'iframe',
  'embed',
  'object',
  'frame',
  'frameset',
];

export const stripUnwantedHtmlTags = (
  html: string,
  tagsToRemove: string[] = DEFAULT_UNWANTED_TAGS,
) => DOMPurify.sanitize(html, { FORBID_TAGS: tagsToRemove });

const setSendByDate = (
  values?: CampaignFormValues,
  transformDates?: boolean,
) => {
  if (!transformDates)
    return values?.periodType === CampaignEmailingEnum.BLAST
      ? null
      : values?.sendByDate;

  if (
    values?.periodType === CampaignEmailingEnum.BLAST ||
    !checkIfNowOrAfter(values?.launchDate) ||
    checkIfDatesAreEqual(values?.launchDate, values?.sendByDate)
  )
    return null;

  return !checkIfNowOrAfter(values?.sendByDate)
    ? new Date()
    : values?.sendByDate;
};

const setLaunchDate = (
  values?: CampaignFormValues,
  transformDates?: boolean,
) => {
  if (!transformDates) return values?.launchDate;

  return values?.launchDate &&
    checkIfDateIsPastDate(new Date(), values.launchDate)
    ? new Date()
    : values?.launchDate;
};

const setCompleteAfter = (
  values?: CampaignFormValues,
  transformDates?: boolean,
) => {
  if (!transformDates) return values?.completeAfter;

  return values?.completeAfter &&
    checkIfDateIsPastDate(new Date(), values.completeAfter)
    ? new Date()
    : values?.completeAfter;
};

export const isWithinHourCheck = (time: Date | undefined): boolean => {
  if (!time) return false;
  const launchTime = moment(time);
  const currentDate = moment();

  const diffInHours = launchTime.diff(currentDate, 'hours');

  return diffInHours < 1 ? true : false;
};

export const isBeforeJobStartCheck = (time: Date | undefined): boolean => {
  // Job starts at 5am the next day
  if (!time) return false;
  const launchTime = moment(time);
  const currentDate = moment();

  const tomorrowAt5AM = currentDate
    .clone()
    .startOf('day')
    .utc(true)
    .add(1, 'day')
    .add(5, 'hours');

  const isBeforeTomorrowAt5AM = launchTime.isBefore(tomorrowAt5AM);

  return isBeforeTomorrowAt5AM;
};

export const prepareRequestData = (
  accountId: string,
  values?: CampaignFormValues,
  transformDates?: boolean,
): SaveCampaignRequest => {
  const launchDate = setLaunchDate(values, transformDates);
  const completeAfter = setCompleteAfter(values, transformDates);
  const sendByDate = setSendByDate(values, transformDates);

  return {
    encodeEmailTemplateImages: values?.encodeEmailTemplateImages,
    name: values?.name,
    emailTemplate: values?.emailTemplate,
    landingPage: values?.landingPage,
    launchDate,
    sendByDate,
    completeAfter,
    sendingProfile: values?.sendingProfile?.id,
    url: undefined,
    account: accountId,
    users: values?.targetGroup?.usersList.map((user) => user.id),
    targets: {
      branches: getTargetEntityIdsByType(
        values?.targets ?? [],
        CourseEntityEnrollEnum.BRANCHES,
      ),
      groups: getTargetEntityIdsByType(
        values?.targets ?? [],
        CourseEntityEnrollEnum.GROUPS,
      ),
      users: getTargetEntityIdsByType(
        values?.targets ?? [],
        CourseEntityEnrollEnum.USERS,
      ),
    },
    excludes: {
      branches: getTargetEntityIdsByType(
        values?.excludes ?? [],
        CourseEntityEnrollEnum.BRANCHES,
      ),
      groups: getTargetEntityIdsByType(
        values?.excludes ?? [],
        CourseEntityEnrollEnum.GROUPS,
      ),
      users: getTargetEntityIdsByType(
        values?.excludes ?? [],
        CourseEntityEnrollEnum.USERS,
      ),
    },
    isPublished: values?.isPublished,
    isWhitelistingTest: values?.isWhitelistingTest,
    useDynamicTarget: values?.useDynamicTarget,
    language: values?.language,
    difficulty: values?.difficulty,
    senderProfileType: values?.senderProfileType,
    clickedLinksPercentage: values?.clickedLinksPercentage,
    submittedDataPercentage: values?.submittedDataPercentage,
    description: values?.description,
    benchmark: values?.benchmark?.id,
    phishingSign: values?.phishingSign,
    thumbnail: values?.thumbnail || undefined,
    isAutoPhishing: values?.isAutoPhishing,
    masterTemplate: values?.masterTemplate ?? null,
  };
};

export const getCampaignSubmitValueByStep = (
  step: number,
  data: SaveCampaignRequest,
  isNewPhishingSelectorEnabled: boolean,
) => {
  const isDynamicTargetSetWithinAnHour =
    isWithinHourCheck(data?.launchDate) && !!data?.useDynamicTarget;

  const modifiedData: { [key: number]: any } = {
    0: {
      encodeEmailTemplateImages: data?.encodeEmailTemplateImages,
      emailTemplate: data?.emailTemplate,
      name: data?.name,
    },
    1: {
      landingPage: data?.landingPage,
    },
    2: {
      sendingProfile: data?.sendingProfile,
    },
    3: {
      ...(isNewPhishingSelectorEnabled
        ? {
            targets: data.targets,
            excludes: data.excludes,
            useDynamicTarget: data.useDynamicTarget,
          }
        : { users: data?.users }),
    },
    4: {
      name: data?.name,
      launchDate: data?.launchDate,
      completeAfter: data?.completeAfter,
      sendByDate: data?.sendByDate,
      ...(isNewPhishingSelectorEnabled
        ? isDynamicTargetSetWithinAnHour
          ? { useDynamicTarget: false }
          : { useDynamicTarget: data.useDynamicTarget }
        : {}),
      isAutoPhishing: data?.isAutoPhishing,
    },
  };

  return { ...modifiedData[step], account: data.account };
};

export const getCampaignTemplateSubmitValueByStep = (
  step: number,
  data: SaveCampaignRequest,
) => {
  const modifiedData: { [key: number]: any } = {
    0: {
      encodeEmailTemplateImages: data?.encodeEmailTemplateImages,
      emailTemplate: data?.emailTemplate,
      name: data?.name,
    },
    1: {
      landingPage: data?.landingPage,
    },
    2: {
      sendingProfile: data?.sendingProfile,
    },
    3: {
      name: data?.name,
      isPublished: data?.isPublished,
      isWhitelistingTest: data?.isWhitelistingTest,
      language: data?.language,
      difficulty: data?.difficulty,
      senderProfileType: data?.senderProfileType,
      clickedLinksPercentage: data?.clickedLinksPercentage,
      submittedDataPercentage: data?.submittedDataPercentage,
      description: data?.description,
      benchmark: data?.benchmark,
      phishingSign: data?.phishingSign,
      thumbnail: data?.thumbnail ?? null,
      masterTemplate: data?.masterTemplate ?? null,
    },
  };

  return { ...modifiedData[step], account: data.account };
};

export const setCampaignInitialValues = (
  campaign: Campaign,
  includedEntities: TargetEntity[],
  excludedEntities: TargetEntity[],
): CampaignFormValues => ({
  encodeEmailTemplateImages: campaign?.meta?.encodeEmailTemplateImages,
  emailTemplate: campaign?.emailTemplate?.id,
  landingPage: campaign?.landingPage?.id,
  targets: includedEntities,
  excludes: excludedEntities,
  useDynamicTarget:
    campaign.useDynamicTarget === null && campaign.isIndividual !== true
      ? true
      : !!campaign?.useDynamicTarget,
  sendingProfile: campaign?.sendingProfile,
  benchmark: campaign?.benchmark,
  periodType: campaign?.sendByDate
    ? CampaignEmailingEnum.DRIP
    : CampaignEmailingEnum.BLAST,
  sendByDate: campaign?.sendByDate && new Date(campaign.sendByDate),
  launchDate: campaign?.launchDate && new Date(campaign.launchDate),
  completeAfter: campaign?.completeAfter && new Date(campaign.completeAfter),
  name: campaign?.name,
  meta: campaign?.meta,
  isAutoPhishing: campaign?.isAutoPhishing,
});

export const setCampaignTemplateInitialValues = (
  campaign: CampaignTemplate,
): CampaignFormValues => ({
  encodeEmailTemplateImages: campaign?.meta?.encodeEmailTemplateImages,
  emailTemplate: campaign?.emailTemplate?.id,
  landingPage: campaign?.landingPage?.id,
  sendingProfile: campaign?.sendingProfile,
  name: campaign?.name,
  language: campaign?.language ?? LanguagesEnum.EN,
  difficulty: campaign?.difficulty ?? undefined,
  senderProfileType: campaign?.senderProfileType ?? SenderProfileType.INTERNAL,
  clickedLinksPercentage: campaign?.clickedLinksPercentage ?? undefined,
  submittedDataPercentage: campaign?.submittedDataPercentage ?? undefined,
  description: campaign?.description ?? '',
  isPublished: campaign?.isPublished ?? false,
  isWhitelistingTest: campaign?.type === CampaignType.WHITELISTING_TEST,
  benchmark: campaign?.benchmark,
  phishingSign: campaign?.meta?.phishingSign,
  meta: campaign?.meta,
  benchmarkSummary: campaign?.benchmarkSummary,
  thumbnail: campaign?.thumbnail?.id,
  masterTemplate: campaign?.masterTemplate?.id ?? null,
});

export const getCampaignStatusOptions = (t: TranslationFunctionType) => [
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.ACTIVE]),
    value: CampaignStatusesEnum.ACTIVE,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.DRAFT]),
    value: CampaignStatusesEnum.DRAFT,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.SCHEDULED]),
    value: CampaignStatusesEnum.SCHEDULED,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.COMPLETED]),
    value: CampaignStatusesEnum.COMPLETED,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.PENDING_LAUNCH]),
    value: CampaignStatusesEnum.PENDING_LAUNCH,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.PENDING_COMPLETION]),
    value: CampaignStatusesEnum.PENDING_COMPLETION,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.FAILED_LAUNCH]),
    value: CampaignStatusesEnum.FAILED_LAUNCH,
  },
  {
    label: t(CampaignStatusMessages[CampaignStatusesEnum.FAILED_COMPLETION]),
    value: CampaignStatusesEnum.FAILED_COMPLETION,
  },
];

export const getCampaignUserResultStatuses = (
  t: TranslationFunctionType,
  showEmailOpened?: boolean,
  hasReportedData?: boolean,
) =>
  [
    {
      label: t(
        CampaignUserResultMessages[
          CampaignUserResultStatusesEnum.CAMPAIGN_CREATED
        ],
      ),
      value: [CampaignUserResultStatusesEnum.CAMPAIGN_CREATED],
    },
    {
      label: t(
        CampaignUserResultMessages[CampaignUserResultStatusesEnum.EMAIL_SENT],
      ),
      value: showEmailOpened
        ? CampaignUserResultStatusesEnum.EMAIL_SENT
        : [
            CampaignUserResultStatusesEnum.EMAIL_SENT,
            CampaignUserResultStatusesEnum.EMAIL_OPENED,
          ],
    },
    showEmailOpened
      ? {
          label: t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.EMAIL_OPENED
            ],
          ),
          value: CampaignUserResultStatusesEnum.EMAIL_OPENED,
        }
      : {},
    {
      label: t(
        CampaignUserResultMessages[CampaignUserResultStatusesEnum.CLICKED_LINK],
      ),
      value: CampaignUserResultStatusesEnum.CLICKED_LINK,
    },
    {
      label: t(
        CampaignUserResultMessages[
          CampaignUserResultStatusesEnum.SUBMITTED_DATA
        ],
      ),
      value: CampaignUserResultStatusesEnum.SUBMITTED_DATA,
    },
    hasReportedData
      ? {
          label: t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.EMAIL_REPORTED
            ],
          ),
          value: CampaignUserResultStatusesEnum.EMAIL_REPORTED,
        }
      : {},
    {
      label: t(
        CampaignUserResultMessages[CampaignUserResultStatusesEnum.ERROR],
      ),
      value: CampaignUserResultStatusesEnum.ERROR,
    },
    {
      label: t(
        CampaignUserResultMessages[CampaignUserResultStatusesEnum.SCHEDULED],
      ),
      value: CampaignUserResultStatusesEnum.SCHEDULED,
    },
    {
      label: t(
        CampaignUserResultMessages[CampaignUserResultStatusesEnum.SENDING],
      ),
      value: CampaignUserResultStatusesEnum.SENDING,
    },
  ].filter((item) => !isEmpty(item));

export const getPhishingSignOptions = (t: TranslationFunctionType) =>
  Object.values(CampaignPhishingSignEnum)
    .map((sign) => ({
      label: t(`campaign.template.${sign}`),
      value: sign,
    }))
    .filter((option) => option.label)
    .sort((a, b) => a.label.localeCompare(b.label));

export const getPhishingSimulationStatusColors = (
  status: CampaignStatusesEnum,
) => {
  switch (status) {
    case CampaignStatusesEnum.ACTIVE:
      return 'var(--green-chart-dark)';
    case CampaignStatusesEnum.SCHEDULED:
      return 'var(--teal-chart)';
    case CampaignStatusesEnum.FAILED_LAUNCH:
      return 'var(--red-main)';
    case CampaignStatusesEnum.FAILED_COMPLETION:
      return 'var(--red-main)';
    default:
      return 'var(--red-brown)';
  }
};

export const replaceEmailTemplatePlaceholders = (html: string) => {
  const template = {
    FirstName: faker.person.firstName(),
    LastName: faker.person.lastName(),
    URL: ' ',
    Tracker: ' ',
    EmailProvider: 'Microsoft',
  };

  const modifiedHtml = html.replace(/{{\.?(.*?)}}/g, (match, p1) => {
    return template[p1 as keyof typeof template] || match;
  });

  return modifiedHtml;
};
